'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef } from 'react';
import { ProcessingDotsIcon } from '@/app/components/icons/ProcessingDotsIcon';
import { API_ORIGIN, resolveApiPath } from '@/app/constants';
import { useFetchStream } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { TaskInfo, ViduV1GetTaskStateReply } from '@/app/types';
import { cn } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { getHomeSubmittedTaskRoute } from './utils';

const HOME_FINISHED_TASK_STATES = new Set<TaskInfo['state']>(['success', 'failed', 'canceled']);
const HOME_TASK_STATE_URL = resolveApiPath('/vidu/v1/tasks/state');

interface HomeGenerationProgressPillProps {
  submittedTasks: TaskInfo[];
  processingTaskCount?: number;
  fallbackTaskType?: TaskInfo['type'];
  className?: string;
  compact?: boolean;
  variant?: 'pill' | 'desktopInline';
  /** 关闭「从上到下」的入场位移，只保留淡入。 */
  disableEnterMotion?: boolean;
  /** 内容（文字/流光/icon）默认降透明度，hover 时恢复 100%。 */
  dimContentUntilHover?: boolean;
}

const HomeGenerationTaskStateWatcher = ({ taskId }: { taskId: string }) => {
  const updateTask = useStore((state) => state.updateTask);
  const task = useStore((state) => state.taskList.find((item) => item.id === taskId));
  const shouldKeepSSERef = useRef(false);

  const { run: getTaskState, close: closeGetTaskState } = useFetchStream<{ id: string }>(HOME_TASK_STATE_URL, {
    baseURL: API_ORIGIN,
    onMessage: (str) => {
      const dataObjects = str
        .split('data:')
        .map((d) => d.trim())
        .filter((d) => d);

      for (const dataString of dataObjects) {
        try {
          const json = JSON.parse(dataString) as ViduV1GetTaskStateReply;
          if (!json.state) {
            continue;
          }

          updateTask(taskId, { state: json.state, err_code: json.err_code });
          shouldKeepSSERef.current = !HOME_FINISHED_TASK_STATES.has(json.state);

          if (!shouldKeepSSERef.current) {
            closeGetTaskState();
          }
        } catch (error) {
          console.error('Error_from_home_generation_task_state: ', error);
        }
      }
    },
    onClose: () => {
      if (shouldKeepSSERef.current) {
        getTaskState({ id: taskId });
      }
    },
  });

  useEffect(() => {
    if (task && HOME_FINISHED_TASK_STATES.has(task.state)) {
      shouldKeepSSERef.current = false;
      closeGetTaskState();
      return;
    }

    shouldKeepSSERef.current = true;
    getTaskState({ id: taskId });

    return () => {
      shouldKeepSSERef.current = false;
      closeGetTaskState();
    };
  }, [taskId, task?.state, getTaskState, closeGetTaskState]);

  return null;
};

export const HomeGenerationProgressPill = ({
  submittedTasks,
  processingTaskCount = 0,
  fallbackTaskType,
  className,
  compact = false,
  variant = 'pill',
  disableEnterMotion = false,
  dimContentUntilHover = false,
}: HomeGenerationProgressPillProps) => {
  const t = useTranslations();
  const router = useRouter();
  const taskList = useStore((state) => state.taskList);
  const isDesktopInline = variant === 'desktopInline';
  const activeTasks = useMemo(
    () =>
      submittedTasks.filter((submittedTask) => {
        const task = taskList.find((item) => item.id === submittedTask.id);
        return !task || !HOME_FINISHED_TASK_STATES.has(task.state);
      }),
    [submittedTasks, taskList],
  );
  const activeCount = Math.max(processingTaskCount, activeTasks.length);
  const latestSubmittedTask = submittedTasks.at(-1);
  const goToSubmittedTask = () => router.push(getHomeSubmittedTaskRoute(latestSubmittedTask?.type, fallbackTaskType));

  return (
    <>
      {activeTasks.map((task) => (
        <HomeGenerationTaskStateWatcher key={task.id} taskId={task.id} />
      ))}
      <AnimatePresence>
        {activeCount > 0 && (
          <motion.div
            initial={disableEnterMotion ? false : { opacity: 0, y: isDesktopInline ? -20 : -60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: disableEnterMotion ? 1 : 0, y: disableEnterMotion ? 0 : isDesktopInline ? -20 : -60 }}
            transition={{ duration: disableEnterMotion ? 0 : 0.3, ease: 'easeOut' }}
            role="button"
            tabIndex={0}
            onClick={goToSubmittedTask}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                goToSubmittedTask();
              }
            }}
            className={cn(
              'items-center text-system-white',
              'cursor-pointer',
              // hover 时内容整体恢复 100%（配合 dimContentUntilHover）
              'group/progress',
              isDesktopInline
                ? 'mx-3 flex h-20 gap-2 rounded-b-[28px] border border-white/[0.08] bg-[#1A1C20]/[0.48] px-5 pt-10 pb-2 shadow-[0_18px_60px_rgba(0,0,0,0.24)] backdrop-blur-[60px] transition-colors hover:bg-[#1A1C20]/[0.64]'
                : 'inline-flex max-w-[min(484px,calc(100vw-32px))] border border-white/[0.08] bg-white/[0.04] shadow-[0_18px_60px_rgba(0,0,0,0.24)] backdrop-blur-[30px]',
              !isDesktopInline && (compact ? 'h-8 gap-2 rounded-full px-3' : 'h-11 gap-2 rounded-16 px-3'),
              className,
            )}
          >
            <div
              className={cn(
                'flex shrink-0 items-center transition-colors',
                isDesktopInline
                  ? 'size-5 text-system-white80 group-hover/progress:text-system-white'
                  : cn('text-system-white', compact ? 'size-4' : 'size-5'),
                dimContentUntilHover && 'opacity-60 transition-opacity group-hover/progress:opacity-100',
              )}
            >
              <ProcessingDotsIcon />
            </div>
            <span
              className={cn(
                'min-w-0 truncate leading-none',
                // agent「思考中」同款文字流光动画
                'animate-shimmer-flow-slow bg-[length:200%_100%] bg-[linear-gradient(90deg,rgba(255,255,255,0.3)_0%,#FFF_20%,rgba(255,255,255,0.3)_40%,rgba(255,255,255,0.3)_60%,#FFF_80%,rgba(255,255,255,0.3)_100%)] light:bg-[linear-gradient(90deg,rgba(0,0,0,0.3)_0%,#FFF_20%,rgba(0,0,0,0.3)_40%,rgba(0,0,0,0.3)_60%,#FFF_80%,rgba(0,0,0,0.3)_100%)] bg-clip-text text-transparent',
                isDesktopInline ? 'flex-1 font-normal text-sm' : 'font-medium',
                !isDesktopInline && (compact ? 'text-xs' : 'text-sm'),
                dimContentUntilHover && 'opacity-60 transition-opacity group-hover/progress:opacity-100',
              )}
            >
              {t('home.generationForm.progress.generating', { count: activeCount })}
            </span>
            <button
              type="button"
              className={cn(
                'shrink-0 whitespace-nowrap text-system-white48 leading-none transition-colors hover:text-system-white group-hover/progress:text-system-white',
                isDesktopInline ? 'ml-4 text-sm' : compact ? 'text-xs' : 'text-sm',
                dimContentUntilHover && 'opacity-60 transition-opacity group-hover/progress:opacity-100',
              )}
              onClick={(event) => {
                event.stopPropagation();
                goToSubmittedTask();
              }}
            >
              {t('home.generationForm.progress.goCheck')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
